import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@material-ui/core";
import { assetIds, NIE_REGEX, DNI_REGEX, ThemedAsset } from "nemuru-components";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ICON_TITLE_HEIGHT } from "../../../../../../../../config/config";
import IconTitle from "../../../../../../../shared/components/IconTitle";
import MainButton from "../../../../../../../shared/components/MainButton";
import Text from "../../../../../../../shared/components/Text";
import {
  NIE_FIRST_LETTER_VALIDATOR,
  PARTICIPANT_ALLOWED_ROLES,
  PARTICIPANT_RELATION,
} from "../../../../config";
import { getRoleTextIdVariantByParticipant } from "../utils/getRoleTextVariantByParticipant";

export const ParticipantPersonalData = ({
  onSubmitForm,
  data,
  loan,
  setSelectedRole,
}) => {
  const { palette } = useTheme();

  const filteredParticipantAllowedRolesByLoanParticipants = () => {
    const filteredAllowedRoles = [];
    PARTICIPANT_ALLOWED_ROLES.map((role) => {
      if (
        !loan?.onboarding_details.some(
          (participant) => participant.role === role
        )
      ) {
        filteredAllowedRoles.push(role);
      }
    });
    return filteredAllowedRoles;
  };

  const { register, getValues, handleSubmit, errors, watch, control } = useForm(
    {
      defaultValues: {
        participantRole:
          data?.participantRole ||
          filteredParticipantAllowedRolesByLoanParticipants()[0],
        participantFirstName: data?.participantFirstName || null,
        participantLastName: data?.participantLastName || null,
        participantDocumentNumber: data?.participantDocumentNumber || null,
        participantRelation: data?.participantRelation || null,
      },
    }
  );

  const participantRoleWatcher = watch("participantRole");

  const participantDocumentNumberWatcher = watch("participantDocumentNumber");

  const REGEX = participantDocumentNumberWatcher
    ?.charAt(0)
    ?.match(NIE_FIRST_LETTER_VALIDATOR)
    ? NIE_REGEX
    : DNI_REGEX;

  useEffect(() => {
    setSelectedRole(participantRoleWatcher);
  }, [participantRoleWatcher]);

  const currentRoleTextId = getRoleTextIdVariantByParticipant(
    participantRoleWatcher
  );

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_ID_FAMILY_SITUATION}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId={`ADD_${currentRoleTextId}`} />}
          subtitle={<Text tId={`${currentRoleTextId}_PERSONAL_DATA`} />}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="participantRole">
                <Text tId="PARTICIPANT_ROLE" />
              </InputLabel>
              <Controller
                name="participantRole"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: {
                          "aria-label": "Select participant role",
                        },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={errors?.participantRole ? true : false}
                    >
                      {filteredParticipantAllowedRolesByLoanParticipants().map(
                        (role) => (
                          <MenuItem key={role} value={role}>
                            <Text tId={role} />
                          </MenuItem>
                        )
                      )}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors?.participantRole?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="participantRelation">
                <Text tId={`${currentRoleTextId}_RELATION`} />
              </InputLabel>
              <Controller
                name="participantRelation"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: {
                          "aria-label": "Select participant relation",
                        },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={errors.participantRelation ? true : false}
                    >
                      {Object.keys(PARTICIPANT_RELATION).map((relation) => (
                        <MenuItem
                          key={PARTICIPANT_RELATION[relation]}
                          value={PARTICIPANT_RELATION[relation]}
                        >
                          <Text tId={relation} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors.participantRelation?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <TextField
          fullWidth
          label={<Text tId={`${currentRoleTextId}_FIRST_NAME`} />}
          type="text"
          id="participantFirstName"
          name="participantFirstName"
          error={errors.participantFirstName ? true : false}
          helperText={errors.participantFirstName?.message}
          inputProps={{
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }),
          }}
        />
        <TextField
          fullWidth
          label={<Text tId={`${currentRoleTextId}_LAST_NAME`} />}
          type="text"
          id="participantLastName"
          name="participantLastName"
          error={errors.participantLastName ? true : false}
          helperText={errors.participantLastName?.message}
          inputProps={{
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }),
          }}
        />
        <TextField
          fullWidth
          label={<Text tId={`${currentRoleTextId}_DOCUMENT_NUMBER`} />}
          type="text"
          id="participantDocumentNumber"
          name="participantDocumentNumber"
          error={errors.participantDocumentNumber ? true : false}
          helperText={errors.participantDocumentNumber?.message}
          inputProps={{
            max: {
              value: 9,
              message: <Text tId={"INVALID_DOCUMENT_NUMBER"} />,
            },
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
              pattern: {
                value: REGEX,
                message: <Text tId={"INVALID_DOCUMENT_NUMBER"} />,
              },
            }),
          }}
        />
      </Box>
      <Box>
        <MainButton onClick={handleSubmit(() => onSubmitForm(getValues()))} />
      </Box>
    </>
  );
};
